import { EnvironmentService } from '../../services/environment/environment.service';
import { isDefined } from '../variables';

export type Flag =
  | 'SK-5825-cloudprocessing-download-project-zip'
  | 'SK-3938-skymap-dongles'
  | 'SK-4477-access-management'
  | 'SK-5443-beta-pointcloud-pipeline'
  | 'SK-5667-licenses-page'
  | 'SK-2782-length-profile-area-calculation'
  | 'SK-6609-skyview-download'
  | 'SK-6613-signature-processing-package'
  | 'SK-6726-dem'
  | 'SK-6920-pm'
  | 'SK-6974-drone-loader'
  | 'SK-7002-extra-point-cloud-settings'
  | 'SK-7346-zitadel-invitations'
  | 'SK-7512-flexible-snapping'
  | 'SK-7535-design-curves'
  | 'SK-7537-dwg-to-glb'
  | 'SK-7538-ifc-hover-highlight'
  | 'SK-7540-tooltip-in-menu'
  | 'SK-7544-backend-pdf-report'
  | 'SK-7544-report-payload-download'
  | 'SK-7549-layer-loading-status'
  | 'SK-7549-view-settings'
  | 'SK-7558-measure-snapping-options'
  | 'SK-7558-visualize-all-heights-option'
  | 'SK-7562-timeline-bar';

type FlagListener = (flag: Flag, enabled: boolean) => void;

/** The ticket number of a flag, as in SK-1234-name, used to sort flags. */
const ticketNumber = (flag: Flag) => Number(/^SK-(\d+)/.exec(flag)?.[1] ?? -Infinity);

export class FeatureFlagsManager {
  private static _instance: FeatureFlagsManager | undefined;
  private flags!: Record<Flag, boolean>;
  private optionalFlags = new Set<Flag>();
  private listeners = new Set<FlagListener>();

  private constructor() {
    this.initialize();
    this.deleteFlagsFromProduction();
  }

  static get instance() {
    if (!FeatureFlagsManager._instance) {
      FeatureFlagsManager._instance = new FeatureFlagsManager();
    }
    return FeatureFlagsManager._instance;
  }

  private initialize() {
    this.flags = {
      ...this.resolveFlag('SK-5825-cloudprocessing-download-project-zip', 'optional'),
      ...this.resolveFlag('SK-3938-skymap-dongles', 'optional'),
      ...this.resolveFlag('SK-5443-beta-pointcloud-pipeline', 'optional'),
      ...this.resolveFlag('SK-4477-access-management', 'optional'),
      ...this.resolveFlag('SK-5667-licenses-page', 'optional'),
      ...this.resolveFlag('SK-2782-length-profile-area-calculation', 'optional'),
      ...this.resolveFlag('SK-6609-skyview-download', 'optional'),
      ...this.resolveFlag('SK-6613-signature-processing-package', 'optional'),
      ...this.resolveFlag('SK-6726-dem', 'optional'),
      ...this.resolveFlag('SK-6920-pm', 'optional'),
      ...this.resolveFlag('SK-6974-drone-loader', 'optional'),
      ...this.resolveFlag('SK-7002-extra-point-cloud-settings', 'optional'),
      ...this.resolveFlag('SK-7346-zitadel-invitations', 'optional'),
      ...this.resolveFlag('SK-7512-flexible-snapping', 'optional'),
      ...this.resolveFlag('SK-7535-design-curves', 'optional'),
      ...this.resolveFlag('SK-7537-dwg-to-glb', 'optional'),
      ...this.resolveFlag('SK-7538-ifc-hover-highlight', 'optional'),
      ...this.resolveFlag('SK-7540-tooltip-in-menu', 'optional'),
      ...this.resolveFlag('SK-7544-backend-pdf-report', 'optional'),
      ...this.resolveFlag('SK-7544-report-payload-download', 'optional'),
      ...this.resolveFlag('SK-7549-layer-loading-status', 'optional'),
      ...this.resolveFlag('SK-7549-view-settings', 'optional'),
      ...this.resolveFlag('SK-7558-measure-snapping-options', 'optional'),
      ...this.resolveFlag('SK-7558-visualize-all-heights-option', 'optional'),
      ...this.resolveFlag('SK-7562-timeline-bar', 'optional'),
    };

    this.pushFeatureFlagsToNative();
  }

  /**
   * For a few months we hade NODE_ENV set to "development" in the production environment. This
   * resulted in all flags being created automatically in the production environment (should only be
   * created automatically in development environment).
   *
   * This function will delete affected feature flags from customers Local Storage, and only if
   * feature flag is not set to "true". If it's "true" customer has set it manually and the flag
   * should then not be deleted.
   *
   * Deleting these flags will prevent customers from easily enabling them by looking in Local
   * Storage.
   */
  private deleteFlagsFromProduction() {
    if (EnvironmentService.instance.isProduction()) {
      const flags: Flag[] = ['SK-3938-skymap-dongles', 'SK-4477-access-management'];
      for (const flag of flags) {
        const value = localStorage.getItem(flag);
        if (value !== 'true') {
          localStorage.removeItem(flag);
        }
      }
    }
  }

  /**
   * Returns a list of all feature flags and a boolean value indicating whether flag is enabled.
   */
  getAll() {
    return this.flags;
  }

  /**
   *  Returns a value indicating whether the specified flag is enabled.
   */
  isEnabled(flagName: Flag) {
    return this.flags[flagName];
  }

  /**
   * Returns the flags that can be turned on and off, the newest ticket first. Released and disabled
   * flags are left out, as their value is forced.
   */
  getOptional() {
    return [...this.optionalFlags].sort(
      (a, b) => ticketNumber(b) - ticketNumber(a) || a.localeCompare(b),
    );
  }

  /**
   * Turns an optional flag on or off, saves it in Local Storage so it's kept after a reload, and
   * notifies the listeners. Code that read the flag earlier doesn't see the change until a reload.
   */
  setEnabled(flag: Flag, enabled: boolean) {
    if (!this.optionalFlags.has(flag)) {
      return;
    }

    try {
      localStorage.setItem(flag, enabled.toString());
    } catch {
      // The flag is then only toggled for this session, e.g. where storage is blocked or full.
    }

    this.flags[flag] = enabled;
    this.pushFeatureFlagsToNative();
    this.listeners.forEach((listener) => listener(flag, enabled));
  }

  /**
   * Calls the listener whenever a flag is turned on or off. Returns a function that unsubscribes.
   */
  subscribe(listener: FlagListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   *  Pushes the feature flags state to the native interface-
   */
  pushFeatureFlagsToNative() {
    // @ts-ignore
    window.iosCallbacks?.pushFeatureFlags();
  }

  /**
   * Returns a record with the specified flag and a value indicating whether the flag is enabled.
   * This enables us to spread it into the final Record containing all flags. Using this approach
   * we don't need to specify the flag twice when initializing the flags property.
   *
   * If parameter state is set to either released/disabled, this value will be used regardless of
   * whether the flag exists in Local Storage or not. Useful if flag needs to be reverted. Then
   * any customer added feature flag value will be overridden.
   *
   * Otherwise, the value is fetched from Local storage (optional). If flag doesn't exist in Local
   * Storage it's saved in Local Storage to false for non-production environments. This makes it
   * easier for developers and other colleagues to see what feature flags are available in developer
   * stacks and in development.
   * @param flag Name of feature flag.
   * @param state Whether the flag should be optional or forced released/disabled.
   */
  private resolveFlag<F extends Flag>(
    flag: F,
    state: 'optional' | 'released' | 'disabled',
  ): Record<F, boolean> {
    const value = state === 'optional' ? undefined : state === 'released';
    const savedValue = localStorage.getItem(flag);

    if (state === 'optional') {
      this.optionalFlags.add(flag);
    }

    if (savedValue === null && !EnvironmentService.instance.isProduction()) {
      localStorage.setItem(flag, value?.toString() ?? 'false');
    }

    return {
      [flag]: isDefined(value) ? value : savedValue === 'true',
    } as Record<F, boolean>;
  }
}
